/** `null` marks an engine we recognise that has no connection-URL form. */
export const DATABASE_ENGINE_SCHEMES: Record<string, string | null> = {
    PostgreSQL: 'postgresql',
    MySQL: 'mysql',
    MariaDB: 'mariadb',
    MongoDB: 'mongodb',
    'Microsoft SQL Server': 'mssql',
    Oracle: 'oracle',
    Redis: 'redis',
    ClickHouse: 'clickhouse',
    Cassandra: 'cassandra',
    CockroachDB: 'postgresql',
    SQLite: null,
    'Amazon Aurora': null,
    'Amazon RDS': null,
    'Azure SQL': null,
};

const DATABASE_ENGINE_ALIASES: Record<string, string> = {
    pg: 'postgresql',
    psql: 'postgresql',
    postgres: 'postgresql',
    mongo: 'mongodb',
    mssql: 'mssql',
    sqlserver: 'mssql',
};

const normalize = (databaseType: string) => databaseType.toLowerCase().replace(/[^a-z0-9]/g, '');

const schemeByKey: Record<string, string | null> = {
    ...Object.fromEntries(Object.entries(DATABASE_ENGINE_SCHEMES).map(([label, scheme]) => [normalize(label), scheme])),
    ...DATABASE_ENGINE_ALIASES,
};

export type DatabaseSchemeResolution = { outcome: 'resolved'; scheme: string } | { outcome: 'unsupported' } | { outcome: 'unknown' };

const lookupKey = (databaseType: string) => {
    const key = normalize(databaseType);

    if (key in schemeByKey) {
        return key;
    }

    const withoutVersion = key.replace(/\d+$/, '');

    return withoutVersion in schemeByKey ? withoutVersion : undefined;
};

export const resolveDatabaseScheme = (databaseType?: string | null): DatabaseSchemeResolution => {
    const key = lookupKey(databaseType ?? '');

    if (key === undefined) {
        return { outcome: 'unknown' };
    }

    const scheme = schemeByKey[key];

    return scheme === null ? { outcome: 'unsupported' } : { outcome: 'resolved', scheme };
};

export interface DatabaseConnectionFields {
    databaseType?: string | null;
    server?: string | null;
    // The API returns the port as a number (openapi.yml), while the form schema stores it as a string.
    port?: string | number | null;
    databaseName?: string | null;
    username?: string | null;
    password?: string | null;
}

/** A bracketed IPv6 literal is full of colons, so only a colon outside the brackets marks a port. */
const carriesPort = (host: string) => (host.startsWith('[') ? /^\[[^\]]*\]:\d+$/ : /^[^:]*:\d+$/).test(host);

export const buildDatabaseConnectionUrl = ({ databaseType, server, port, databaseName, username, password }: DatabaseConnectionFields) => {
    const resolution = resolveDatabaseScheme(databaseType);
    const host = server?.trim().replace(/^[a-z][a-z0-9+.-]*:\/\//i, '');

    if (resolution.outcome !== 'resolved' || !host) {
        return undefined;
    }

    const user = username?.trim();
    const pass = password?.trim();
    const name = databaseName?.trim();
    const portNumber = port?.toString().trim();

    const userinfo = user ? `${encodeURIComponent(user)}${pass ? `:${encodeURIComponent(pass)}` : ''}@` : '';
    const portSegment = portNumber && /^\d+$/.test(portNumber) && !carriesPort(host) ? `:${portNumber}` : '';
    const path = name ? `/${encodeURIComponent(name)}` : '';

    return `${resolution.scheme}://${userinfo}${host}${portSegment}${path}`;
};
